import React, { createContext, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import posthog from 'posthog-js'
import { get as getConfig } from 'config'

const FeatureFlagsContext = createContext({})

export const useFeatureFlags = () => useContext(FeatureFlagsContext)

const FlagsBridge = ({ children }) => {
  const [flags, setFlags] = useState(() => posthog.featureFlags?.getFlagVariants?.() || {})

  useEffect(
    () =>
      posthog.onFeatureFlags((_flagKeys, flagVariants) => {
        setFlags(flagVariants || {})
      }),
    [],
  )

  return <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>
}

const PostHogIdentitySync = () => {
  const identity = useSelector(state => state.identity)

  useEffect(() => {
    if (identity.signedIn && identity.email) {
      // Use email as the distinct id to match server-routed TrackEventCommand
      // call sites, which all set UserId = email. Diverging here would split
      // the same user across two PostHog persons.
      posthog.identify(identity.email, {
        user_id: identity.userId,
        candidate_id: identity.candidateId,
        employer_user_id: identity.employerUserId,
      })
    } else {
      posthog.reset()
    }
    // Re-identify when any of the $set properties change. Candidate/employer
    // ids often populate after login via a follow-up redux action (e.g.
    // fetchGetCandidate), so a deps array of just [signedIn, email] would
    // leave PostHog's person record stale.
  }, [identity.signedIn, identity.email, identity.userId, identity.candidateId, identity.employerUserId])

  return null
}

export const initializePostHog = async () => {
  const projectToken = getConfig('posthogProjectToken', null)
  const apiHost = getConfig('posthogHost', 'https://us.i.posthog.com')

  if (!projectToken) {
    console.warn('PostHog project token not configured. Analytics and feature flags will be disabled.')
    return ({ children }) => <>{children}</>
  }

  try {
    posthog.init(projectToken, {
      api_host: apiHost,
      autocapture: true,
      // 'history_change' (not the boolean `true`) is required for posthog-js to
      // emit $pageview on SPA route changes via history.pushState / popstate.
      // With `true`, only the initial document load is captured, which would
      // miss almost all in-app navigation in this React Router app.
      capture_pageview: 'history_change',
      person_profiles: 'identified_only',
    })

    return ({ children }) => (
      <FlagsBridge>
        <PostHogIdentitySync />
        {children}
      </FlagsBridge>
    )
  } catch (error) {
    console.error('Failed to initialize PostHog:', error)
    return ({ children }) => <>{children}</>
  }
}
